// TipTap JSON → React renderer for public Helpdesk pages.
// Allowlist by construction: only known node types render, React escapes all
// text, and no HTML string is ever injected. Mirrors the server-side
// validateTipTap allowlist (kora_api/src/services/kb.js).
// U-08: heading links · U-09: code copy button

import { useMemo, useState } from 'react';
import type { KbTipTapDoc } from '@/lib/real-api';
import { kbMediaUrl } from '@/lib/kb-public';
import { cn } from '@/lib/utils';

type Mark = { type: string; attrs?: Record<string, unknown> };

function slugifyHeading(text: string): string {
  return text.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'heading';
}

function embedUrl(src: string): string | null {
  try {
    const u = new URL(src);
    const host = u.hostname.toLowerCase().replace(/^(www|m|player)\./, '');
    if (host === 'youtu.be') return `https://www.youtube.com/embed${u.pathname}`;
    if (host === 'youtube.com') {
      if (u.pathname === '/watch') return `https://www.youtube.com/embed/${u.searchParams.get('v') ?? ''}`;
      if (u.pathname.startsWith('/embed/')) return u.toString();
      if (u.pathname.startsWith('/shorts/')) return `https://www.youtube.com/embed${u.pathname.slice('/shorts'.length)}`;
    }
    if (host === 'vimeo.com') return `https://player.vimeo.com/video${u.pathname}`;
    if (host === 'loom.com') return u.toString().replace('/share/', '/embed/');
    return null;
  } catch {
    return null;
  }
}

function MarkedText({ text, marks }: { text: string; marks?: Mark[] }) {
  let node: React.ReactNode = text;
  for (const mark of marks ?? []) {
    if (mark.type === 'bold') node = <strong className="font-semibold">{node}</strong>;
    else if (mark.type === 'italic') node = <em>{node}</em>;
    else if (mark.type === 'underline') node = <u>{node}</u>;
    else if (mark.type === 'strike') node = <s className="opacity-70">{node}</s>;
    else if (mark.type === 'code') {
      node = <code className="rounded bg-zinc-100 px-1.5 py-0.5 font-mono text-[0.85em] text-sky-700 dark:bg-zinc-800 dark:text-sky-300">{node}</code>;
    } else if (mark.type === 'link') {
      const href = typeof mark.attrs?.href === 'string' ? mark.attrs.href : '#';
      node = (
        <a href={href} target="_blank" rel="noopener noreferrer nofollow" className="underline underline-offset-2 hover:opacity-80">
          {node}
        </a>
      );
    }
  }
  return <>{node}</>;
}

export interface ArticleRendererProps {
  doc: KbTipTapDoc;
  assetSigs?: Record<string, string>;
  className?: string;
}

export function ArticleRenderer({ doc, assetSigs, className }: ArticleRendererProps) {
  return (
    <div className={cn('kb-article text-[15px] leading-7', className)}>
      {doc.content?.map((node, i) => (
        <Block key={i} node={node} assetSigs={assetSigs} />
      ))}
    </div>
  );
}

/** Extract headings from a TipTap doc for TOC generation (U-07). */
export function extractHeadings(doc: KbTipTapDoc): { level: number; text: string; id: string }[] {
  const headings: { level: number; text: string; id: string }[] = [];
  const walk = (node: KbTipTapDoc) => {
    if (node.type === 'heading') {
      const text = (node.content ?? []).map(c => c.text ?? '').join('');
      if (text) headings.push({ level: Number(node.attrs?.level ?? 2), text, id: slugifyHeading(text) });
    }
    node.content?.forEach(walk);
  };
  walk(doc);
  return headings;
}

/** Add anchor IDs to heading elements for TOC deep-linking (U-08). */
export function useHeadingAnchors(doc: KbTipTapDoc) {
  return useMemo(() => {
    const map = new Map<string, string>();
    const walk = (node: KbTipTapDoc) => {
      if (node.type === 'heading') {
        const text = (node.content ?? []).map(c => c.text ?? '').join('');
        if (text) map.set(text, slugifyHeading(text));
      }
      node.content?.forEach(walk);
    };
    walk(doc);
    return map;
  }, [doc]);
}

function Block({ node, assetSigs }: { node: KbTipTapDoc; assetSigs?: Record<string, string> }) {
  switch (node.type) {
    case 'heading': {
      const level = Number(node.attrs?.level ?? 2);
      const cls = level === 1 ? 'text-3xl font-bold tracking-tight mt-10 mb-4' : level === 2 ? 'text-2xl font-bold tracking-tight mt-10 mb-3' : 'text-lg font-semibold mt-8 mb-2';
      const Tag = (level === 1 ? 'h2' : level === 2 ? 'h3' : 'h4') as 'h2' | 'h3' | 'h4';
      const text = node.content?.map(c => c.text ?? '').join('') ?? '';
      const id = slugifyHeading(text);
      return (
        <Tag id={id} className={cn(cls, 'group scroll-mt-16 relative')}>
          {node.content?.map((c, i) => <MarkedText key={i} text={c.text ?? ''} marks={c.marks} />)}
          <a
            href={`#${id}`}
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById(id);
              el?.scrollIntoView({ behavior: 'smooth' });
              navigator.clipboard.writeText(`${window.location.origin}${window.location.pathname}#${id}`);
            }}
            className="ml-2 opacity-0 group-hover:opacity-40 transition-opacity no-underline"
            aria-label={`Link to ${text}`}
          >
            <svg className="size-4 inline" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
          </a>
        </Tag>
      );
    }
    case 'paragraph':
      return <p className="my-4">{node.content?.length ? node.content.map((c, i) => <MarkedText key={i} text={c.text ?? ''} marks={c.marks} />) : <br />}</p>;
    case 'blockquote':
      return (
        <blockquote className="my-5 border-l-[3px] border-current/20 pl-4 opacity-90 italic">
          {node.content?.map((c, i) => <Block key={i} node={c} assetSigs={assetSigs} />)}
        </blockquote>
      );
    case 'bulletList':
      return (
        <ul className="my-4 list-disc space-y-1.5 pl-6 marker:text-current/50">
          {node.content?.map((c, i) => <Block key={i} node={c} assetSigs={assetSigs} />)}
        </ul>
      );
    case 'orderedList':
      return (
        <ol className="my-4 list-decimal space-y-1.5 pl-6 marker:text-current/50 marker:font-medium">
          {node.content?.map((c, i) => <Block key={i} node={c} assetSigs={assetSigs} />)}
        </ol>
      );
    case 'listItem':
      return <li>{node.content?.map((c, i) => <Block key={i} node={c} assetSigs={assetSigs} />)}</li>;
    case 'codeBlock': {
      const text = (node.content ?? []).map((c) => c.text ?? '').join('');
      return <CodeBlock code={text} language={node.attrs?.language} />;
    }
    case 'horizontalRule':
      return <hr className="my-8 border-current/15" />;
    case 'image': {
      const src = typeof node.attrs?.src === 'string' ? node.attrs.src : '';
      const alt = typeof node.attrs?.alt === 'string' ? node.attrs.alt : '';
      return (
        <img
          src={kbMediaUrl(src, assetSigs)}
          alt={alt}
          loading="lazy"
          className="my-6 w-full rounded-xl border border-current/10"
        />
      );
    }
    case 'videoBlock': {
      const src = typeof node.attrs?.src === 'string' ? node.attrs.src : '';
      if (node.attrs?.kind === 'embed') {
        const embed = embedUrl(src);
        if (!embed) return null;
        return (
          <div className="my-6 aspect-video w-full overflow-hidden rounded-xl border border-current/10">
            <iframe
              src={embed}
              title="Embedded video"
              className="size-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>
        );
      }
      return (
        <video
          controls
          preload="metadata"
          src={kbMediaUrl(src, assetSigs)}
          className="my-6 w-full rounded-xl border border-current/10"
        />
      );
    }
    case 'table':
      return (
        <div className="my-6 overflow-x-auto rounded-xl border border-current/15">
          <table className="w-full border-collapse text-sm">
            <tbody>{node.content?.map((c, i) => <Block key={i} node={c} assetSigs={assetSigs} />)}</tbody>
          </table>
        </div>
      );
    case 'tableRow':
      return <tr className="border-b border-current/10 last:border-0">{node.content?.map((c, i) => <Block key={i} node={c} assetSigs={assetSigs} />)}</tr>;
    case 'tableHeader':
      return <th className="bg-current/5 px-3.5 py-2.5 text-left font-semibold">{node.content?.map((c, i) => <MarkedText key={i} text={c.text ?? ''} marks={c.marks} />)}</th>;
    case 'tableCell':
      return <td className="px-3.5 py-2.5 align-top">{node.content?.map((c, i) => <MarkedText key={i} text={c.text ?? ''} marks={c.marks} />)}</td>;
    default:
      return null;
  }
}

/** U-09: code block with copy button. */
function CodeBlock({ code, language }: { code: string; language?: unknown }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="group/code relative my-5">
      <pre className="overflow-x-auto rounded-xl bg-zinc-950 p-4 text-[13px] leading-6 text-zinc-100">
        <code className="font-mono">{code}</code>
      </pre>
      <button
        onClick={copy}
        aria-label="Copy code"
        className="absolute right-2.5 top-2.5 rounded-md bg-zinc-800/80 px-2 py-1 text-[11px] font-medium text-zinc-300 opacity-0 transition-opacity hover:bg-zinc-700 group-hover/code:opacity-100"
      >
        {copied ? 'Copied ✓' : 'Copy'}
      </button>
    </div>
  );
}
